import React from 'react';
import { Copy, Check, User, Bot, Info, HelpCircle, Star } from 'lucide-react';
import { Message } from '../types';
import { CitationAnchor, StructuredParagraph } from '../types/api';
import { AnswerWithCitations } from './AnswerWithCitations';
import { CitationHighlight } from './CitationHighlight';
import { StructuredAnswer } from './StructuredAnswer';

interface MessageBubbleProps {
  message: Message;
  onCopy?: (content: string) => void;
  isCopied?: boolean;
  onStarPrompt?: (text: string) => void;
  onUnstarPrompt?: (text: string) => void;
  isPromptStarred?: (text: string) => boolean;
  citations?: CitationAnchor[];
  paragraphs?: StructuredParagraph[];
  opinions?: { id: string; polarity?: string }[];
}

export const MessageBubble: React.FC<MessageBubbleProps> = ({ 
  message, 
  onCopy, 
  isCopied, 
  onStarPrompt, 
  onUnstarPrompt, 
  isPromptStarred,
  citations,
  paragraphs,
  opinions
}) => {
  const isUser = message.type === 'user';
  const isStatus = message.type === 'status';
  const isStarred = isUser && isPromptStarred ? isPromptStarred(message.content) : false;
  const hasCitations = !isUser && !isStatus && citations && citations.length > 0;
  const hasParagraphs = !isUser && !isStatus && paragraphs && paragraphs.length > 0;

  const getIcon = () => {
    if (isUser) return <User className="w-4 h-4" />;
    if (isStatus) return <Info className="w-4 h-4" />;
    if (message.needsClarification) return <HelpCircle className="w-4 h-4 text-orange-500" />;
    return <Bot className="w-4 h-4" />;
  };

  const getBubbleStyles = () => {
    if (isUser) {
      return 'bg-user-bubble text-white ml-auto';
    }
    if (isStatus) {
      return 'bg-status-bubble text-gray-700 mx-auto';
    }
    if (message.needsClarification) {
      return 'bg-orange-50 text-gray-900 border border-orange-200';
    }
    return 'bg-yellow-50 text-gray-900 border border-yellow-200';
  };

  const getContainerStyles = () => {
    if (isUser) return 'justify-end';
    if (isStatus) return 'justify-center';
    return 'justify-start';
  };

  return (
    <div data-component="MessageBubble" className={`flex ${getContainerStyles()} mb-4`}>
      <div className={`w-full px-4 py-3 rounded-lg ${getBubbleStyles()} relative group`}>
        <div className="flex items-start gap-2">
          {(isUser || isStatus) && (
            <div className="flex-shrink-0 mt-1">
              {getIcon()}
            </div>
          )}
          <div className="flex-1">
            {!isUser && !isStatus && (
              <div className="font-bold mb-2 text-gray-900">Answer:</div>
            )}
            <div className="whitespace-pre-wrap break-words">
              {hasParagraphs ? (
                <StructuredAnswer
                  paragraphs={paragraphs!}
                  citations={citations}
                  opinions={opinions}
                />
              ) : hasCitations ? (
                <AnswerWithCitations 
                  answer={message.content} 
                  citations={citations!}
                  opinions={opinions}
                />
              ) : isUser || isStatus ? (
                message.content
              ) : (
                <CitationHighlight 
                  answer={message.content} 
                  citations={[]} 
                />
              )}
            </div>
            {message.needsClarification && (
              <div className="mt-2 text-xs text-orange-600 bg-orange-100 px-2 py-1 rounded">
                💡 This message needs clarification. Your next question will include previous context automatically.
              </div>
            )}
            <div className={`text-xs mt-2 opacity-70 ${isUser ? 'text-blue-100' : 'text-gray-500'}`}>
              {message.timestamp.toLocaleTimeString()}
            </div>
          </div>
        </div>
        
        {!isUser && !isStatus && onCopy && (
          <button
            onClick={() => onCopy(message.content)}
            className="absolute top-2 right-2 p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity bg-gray-100 hover:bg-gray-200"
            title="Copy message"
          >
            {isCopied ? (
              <Check className="w-4 h-4 text-green-600" />
            ) : (
              <Copy className="w-4 h-4 text-gray-600" />
            )}
          </button>
        )}

        {isUser && (onStarPrompt || onUnstarPrompt) && (
          <button
            onClick={() => {
              if (isStarred && onUnstarPrompt) {
                onUnstarPrompt(message.content);
              } else if (!isStarred && onStarPrompt) {
                onStarPrompt(message.content);
              }
            }}
            className="absolute top-2 right-2 p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity bg-blue-100 hover:bg-blue-200"
            title={isStarred ? "Remove from favorites" : "Add to favorites"}
          >
            <Star 
              className={`w-4 h-4 ${isStarred ? 'text-yellow-500 fill-yellow-500' : 'text-gray-600'}`} 
            />
          </button>
        )}
      </div>
    </div>
  );
};